import { useEffect } from 'react';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

const steps = [
  { icon: 'ri-phone-line', title: 'Emergency Call', desc: 'Call us 24/7 at (209) 451-2288. Our team is always ready to respond immediately.' },
  { icon: 'ri-search-eye-line', title: 'Inspection & Assessment', desc: 'We assess the extent of water damage using moisture meters and thermal imaging cameras.' },
  { icon: 'ri-drop-line', title: 'Water Extraction', desc: 'Powerful truck-mounted extractors remove standing water from your property fast.' },
  { icon: 'ri-wind-line', title: 'Drying & Dehumidification', desc: 'Industrial air movers and dehumidifiers dry out walls, floors, and structural materials.' },
  { icon: 'ri-shield-check-line', title: 'Sanitization', desc: 'We apply antimicrobial treatments to prevent mold growth and eliminate bacteria.' },
  { icon: 'ri-home-smile-line', title: 'Restoration', desc: 'Full repairs and reconstruction to return your property to its pre-loss condition.' },
];

const reasons = [
  { icon: 'ri-time-line', title: '60-Minute Response', desc: 'We arrive within 60 minutes of your call anywhere in Merced County.' },
  { icon: 'ri-award-line', title: 'IICRC Certified', desc: 'Our technicians hold the highest industry certifications in water damage restoration.' },
  { icon: 'ri-file-list-3-line', title: 'Insurance Billing', desc: 'We handle all insurance paperwork and bill your carrier directly.' },
  { icon: 'ri-tools-line', title: 'Advanced Equipment', desc: 'Truck-mounted extractors, thermal cameras, and commercial-grade drying systems.' },
  { icon: 'ri-map-pin-line', title: 'Locally Owned', desc: 'Proudly serving Merced, CA and the Central Valley since day one.' },
  { icon: 'ri-thumb-up-line', title: '5-Star Rated', desc: 'Hundreds of satisfied homeowners and businesses trust Indoor-Restore Services.' },
];

export default function WaterDamagePage() {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mercedwaterrestoration.com';

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Water Damage Restoration",
      "name": "Water Damage Restoration in Merced, CA",
      "description": "24/7 emergency water extraction, structural drying, and complete water damage restoration services in Merced County. IICRC certified technicians with 60-minute response time.",
      "url": `${siteUrl}/services/water-damage`,
      "provider": {
        "@type": "LocalBusiness",
        "name": "Indoor-Restore Services",
        "telephone": "+12094512288",
        "email": "info@mercedwaterrestoration.com",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "1729 U St",
          "addressLocality": "Merced",
          "addressRegion": "CA",
          "postalCode": "95340",
          "addressCountry": "US"
        }
      },
      "areaServed": {
        "@type": "City",
        "name": "Merced",
        "containedInPlace": {
          "@type": "State",
          "name": "California"
        }
      },
      "availableChannel": {
        "@type": "ServiceChannel",
        "servicePhone": {
          "@type": "ContactPoint",
          "telephone": "+12094512288",
          "contactType": "Emergency Service",
          "availableLanguage": "English",
          "hoursAvailable": "24/7"
        }
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/c64d57f0cf396769189c81f66878995f.jpg"
            alt="Water Damage Restoration Merced CA"
            className="w-full h-full object-cover object-top"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/65"></div>
        </div>
        <div className="relative z-10 text-center px-4 w-full max-w-5xl mx-auto py-24">
          <span className="inline-block bg-teal-500/30 border border-teal-400/50 text-teal-200 text-sm font-semibold px-4 py-1.5 rounded-full mb-5">
            24/7 Emergency Service
          </span>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-5 leading-tight">
            Water Damage Restoration<br />in Merced, CA
          </h1>
          <p className="text-xl text-white/85 mb-8 max-w-2xl mx-auto">
            Fast, professional water extraction and drying services. We minimize damage and restore your property to pre-loss condition.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:2094512288" className="bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-teal-700 transition-all shadow-lg whitespace-nowrap cursor-pointer">
              <i className="ri-phone-line mr-2"></i>Call (209) 451-2288
            </a>
            <a href="/#contact" className="bg-white text-teal-700 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all shadow-lg whitespace-nowrap cursor-pointer">
              Get Free Estimate
            </a>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-5">Expert Water Damage Cleanup You Can Count On</h2>
              <p className="text-gray-600 leading-relaxed mb-5">
                Water damage can strike at any moment — a burst pipe, appliance leak, or flooding event can devastate your home or business within hours. At <strong>Indoor-Restore Services</strong>, we respond fast to stop the damage in its tracks.
              </p>
              <p className="text-gray-600 leading-relaxed mb-5">
                Our IICRC-certified technicians use industrial-grade extraction and drying equipment to remove every trace of moisture from your structure, preventing secondary damage like mold growth and wood rot.
              </p>
              <ul className="space-y-3">
                {['Burst pipe & plumbing leaks', 'Appliance overflow (washer, dishwasher)', 'Roof leaks & rainwater intrusion', 'Sewage backup cleanup', 'Basement & crawl space flooding', 'Storm & flood damage'].map(item => (
                  <li key={item} className="flex items-center gap-3 text-gray-700 text-sm">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-teal-500 text-lg"></i>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl h-96">
              <img
                src="https://readdy.ai/api/search-image?query=water%20damage%20restoration%20crew%20setting%20up%20industrial%20dehumidifiers%20and%20air%20movers%20in%20a%20flooded%20California%20home%20interior%20with%20moisture%20damage%20on%20walls%20and%20ceiling%2C%20professional%20equipment%2C%20clean%20background&width=800&height=600&seq=wd-overview-02&orientation=landscape"
                alt="Water damage restoration equipment setup"
                className="w-full h-full object-cover object-top"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our 6-Step Restoration Process</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">A proven, systematic approach that gets your property back to normal as quickly as possible.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {steps.map((step, i) => (
              <div key={i} className="bg-white rounded-xl p-7 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${step.icon} text-2xl text-teal-600`}></i>
                  </div>
                  <span className="text-3xl font-bold text-teal-100">{String(i + 1).padStart(2, '0')}</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-white mb-4">Why Choose Indoor-Restore Services?</h2>
            <p className="text-white/80 max-w-2xl mx-auto">Merced homeowners and businesses trust us for fast, reliable, and thorough water damage restoration.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {reasons.map((r, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-7 border border-white/20 hover:bg-white/20 transition-all">
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center mb-5">
                  <i className={`${r.icon} text-2xl text-teal-600`}></i>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{r.title}</h3>
                <p className="text-white/75 text-sm leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Dealing With Water Damage Right Now?</h2>
          <p className="text-gray-500 mb-8 text-lg">Don't wait — every minute counts. Call Indoor-Restore Services for immediate emergency response in Merced, CA.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:2094512288" className="bg-teal-600 text-white px-10 py-4 rounded-lg text-lg font-bold hover:bg-teal-700 transition-all shadow-lg whitespace-nowrap cursor-pointer">
              <i className="ri-phone-line mr-2"></i>(209) 451-2288
            </a>
            <a href="mailto:info@mercedwaterrestoration.com" className="border-2 border-teal-600 text-teal-600 px-10 py-4 rounded-lg text-lg font-bold hover:bg-teal-50 transition-all whitespace-nowrap cursor-pointer">
              Email Us
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
